/**
 * "My routine" arithmetic: two doses a day, counted in the member's own calendar.
 *
 * Pure functions, kept apart from the components like `lib/calendar.ts` beside them, so
 * the parts with edges — local days versus UTC instants, the editing window, a time
 * that only counts when it falls on its own day — are unit-testable without a DOM.
 *
 * Every day here is a *local* calendar day (`dayKey`), because that is what the API
 * stores: the evening sachet belongs to Tuesday evening wherever the member took it.
 */

import { dayKey, startOfDay } from "~/lib/calendar";
import type { AuthUser, UserUpdate } from "~/types/auth";
import type { RoutineCheckIn, RoutineSlot } from "~/types/routine";

/** Where the routine lives, and where a reminder tap lands. */
export const ROUTINE_PATH = "/routine";

/** Location state telling the reminder settings to send "back" to the routine. */
export interface RemindersLinkState {
  from: "routine";
}

/** Both doses, in the order a day takes them. */
export const ROUTINE_SLOTS: readonly RoutineSlot[] = ["morning", "evening"];

/**
 * How many days back a dose can still be ticked or unticked. Mirrors
 * `ROUTINE_EDIT_WINDOW_DAYS` in `api/api/services/routine.py`. The API adds a day of
 * timezone slack on top, because it cannot know the member's today; this is the rule
 * the member actually meets.
 */
export const ROUTINE_EDIT_WINDOW_DAYS = 7;

/** One day's doses, whichever of them are in. */
export type RoutineDay = Partial<Record<RoutineSlot, RoutineCheckIn>>;

/** Local midnight `offset` days away. Built from the parts, so DST cannot shift it. */
export function addDays(date: Date, offset: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + offset);
}

/** Whether a day's doses can still be changed: not ahead of today, not past the window. */
export function isEditableDay(day: Date, today: Date): boolean {
  const start = startOfDay(day).getTime();
  return (
    start <= startOfDay(today).getTime() &&
    start >= addDays(today, -ROUTINE_EDIT_WINDOW_DAYS).getTime()
  );
}

/** Whether a day is still to come. */
export function isFutureDay(day: Date, today: Date): boolean {
  return startOfDay(day).getTime() > startOfDay(today).getTime();
}

/** The last `count` days up to and including today, oldest first. */
export function recentDays(today: Date, count: number): Date[] {
  return Array.from({ length: count }, (_, index) =>
    addDays(today, index - count + 1),
  );
}

/** The doses of each day, keyed by `YYYY-MM-DD`. */
export function indexCheckIns(
  items: readonly RoutineCheckIn[],
): Map<string, RoutineDay> {
  const days = new Map<string, RoutineDay>();
  for (const item of items) {
    days.set(item.day, { ...days.get(item.day), [item.slot]: item });
  }
  return days;
}

/** Whether both doses of a day are in. */
export function isCompleteDay(day: RoutineDay | undefined): boolean {
  return ROUTINE_SLOTS.every((slot) => day?.[slot] !== undefined);
}

/**
 * When the dose was taken — but only when the tap happened on the dose's own day.
 *
 * `created_at` is the moment of the tap, not of the sachet. They are the same thing
 * for a dose ticked as it is taken; for last night's dose ticked this morning the tap
 * time would be a lie about the evening, so there is no time to show at all.
 */
export function takenAt(checkIn: RoutineCheckIn): Date | null {
  const instant = new Date(checkIn.created_at);
  return dayKey(instant) === checkIn.day ? instant : null;
}

/** `"07:30"` as hours and minutes, or null for anything that is not a clock time. */
export function parseClockTime(
  value: string,
): { hour: number; minute: number } | null {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  return hour < 24 && minute < 60 ? { hour, minute } : null;
}

/** A clock time on an arbitrary day, for `Intl` to format in the member's convention. */
export function clockTimeAsDate(value: string): Date | null {
  const parsed = parseClockTime(value);
  return parsed ? new Date(2024, 0, 1, parsed.hour, parsed.minute) : null;
}

/** One reminder as the member sees it: a switch and a time. */
export interface ReminderSetting {
  enabled: boolean;
  /** `HH:MM`, local wall-clock time. */
  time: string;
}

/** The member's two reminders, read off the four flat profile fields. */
export function reminderSettings(
  user: AuthUser,
): Record<RoutineSlot, ReminderSetting> {
  return {
    morning: {
      enabled: user.morning_reminder_enabled,
      time: user.morning_reminder_time,
    },
    evening: {
      enabled: user.evening_reminder_enabled,
      time: user.evening_reminder_time,
    },
  };
}

/** The profile update that changes one reminder, in the API's flat field names. */
export function reminderUpdate(
  slot: RoutineSlot,
  change: Partial<ReminderSetting>,
): UserUpdate {
  const update: UserUpdate = {};
  if (change.enabled !== undefined) {
    update[`${slot}_reminder_enabled`] = change.enabled;
  }
  if (change.time !== undefined) update[`${slot}_reminder_time`] = change.time;
  return update;
}
